import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { Container } from "./layout/container";
import LanguageSwitcher from "./LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";

export default function Header() {
  const location = useLocation();
  const { strings } = useLanguage();
  const isHome = location.pathname === "/";
  const isCaseStudy = location.pathname.startsWith("/case-study");
  const isExperienceDetail = location.pathname.startsWith("/experience/") && location.pathname !== "/experience";
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: strings.nav.work, href: "/#projects", isHash: true },
    { label: strings.nav.operatingModel, href: "/#operating-model", isHash: true },
    { label: strings.nav.buildLab, href: "/#build-lab", isHash: true },
    { label: strings.nav.experience, href: "/experience" },
  ];

  // Get page label for sub-pages
  const getPageLabel = () => {
    if (isCaseStudy) return strings.nav.pageLabels.caseStudy;
    if (isExperienceDetail) return strings.nav.pageLabels.experience;
    if (location.pathname === "/contact-me") return strings.nav.pageLabels.contact;
    if (location.pathname === "/experience") return strings.nav.pageLabels.experience;
    if (location.pathname === "/privacy-policy") return strings.nav.pageLabels.privacy;
    if (location.pathname === "/term-condition") return strings.nav.pageLabels.terms;
    return "";
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-200 ${
          isScrolled
            ? "border-white/10 bg-[#0B0B0F]/90 backdrop-blur-xl"
            : "border-white/5 bg-[#0B0B0F]/70 backdrop-blur-xl"
        }`}
      >
        <Container className="flex h-16 items-center justify-between gap-4">
          {/* Left: Logo or Back Button */}
          {isHome ? (
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-tr from-slate-300 to-slate-500 shadow-md shadow-black/50" />
              <span className="text-sm font-semibold tracking-[0.25em] uppercase text-white/70">
                {strings.nav.brand}
              </span>
            </Link>
          ) : (
            <Link
              to={isExperienceDetail ? "/experience" : "/"}
              className="group flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-150"
            >
              <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors">
                <ArrowLeft className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium">{strings.nav.back}</span>
            </Link>
          )}

          {/* Center: Page Label (sub-pages only) */}
          {!isHome && (
            <span className="text-sm font-semibold tracking-wide text-white/80">
              {getPageLabel()}
            </span>
          )}

          {/* Right: Navigation (home) or Controls */}
          <div className="flex items-center gap-3">
            {isHome && (
              <nav className="hidden items-center gap-6 text-xs font-medium text-white/60 md:flex mr-2">
                {navLinks.map((link) =>
                  link.isHash ? (
                    <a
                      key={link.label}
                      href={link.href}
                      className="transition hover:text-white"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="transition hover:text-white"
                    >
                      {link.label}
                    </Link>
                  )
                )}
              </nav>
            )}

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* CTA Button */}
            <Button asChild size="sm" variant={isHome ? "default" : "secondary"} className="shrink-0 hidden sm:inline-flex">
              <Link to="/contact-me">
                <span>{strings.nav.talk}</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>

            {/* Mobile Menu Toggle (Home only) */}
            {isHome && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-white/60 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            )}
          </div>
        </Container>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && isHome && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-40 border-b border-white/10 bg-[#0B0B0F]/95 backdrop-blur-xl md:hidden"
          >
            <Container className="py-4">
              <nav className="flex flex-col gap-2">
                {navLinks.map((link) =>
                  link.isHash ? (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-3 px-4 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-3 px-4 text-sm font-medium text-white/70 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    >
                      {link.label}
                    </Link>
                  )
                )}

                <div className="pt-2 flex items-center justify-between border-t border-white/10 mt-2 px-2">
                  <span className="text-xs text-white/50">Language / 语言 / Ngôn ngữ:</span>
                  <LanguageSwitcher />
                </div>

                <Link
                  to="/contact-me"
                  onClick={() => setMobileMenuOpen(false)}
                  className="mt-2 py-3 px-4 text-sm font-medium text-center bg-white text-black rounded-lg hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
                >
                  {strings.nav.talk}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </nav>
            </Container>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      <div className="h-16" />
    </>
  );
}
